import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastContainerDirective, ToastrService } from 'ngx-toastr';
import { filter, first } from 'rxjs';

import { INTEGRATION } from '@app/integrations/integrations.types';
import { IntegrationsService } from '@app/integrations/services/integrations.service';
import { IconService } from '@core/services/icon.service';
import { FingerprintService } from '@shared/services/fingerprint-service';
import { LayoutConfigService } from '@shared/services/layout/layout-config.service';
import { UiHelperService } from '@shared/services/layout/ui-helper.service';
import { LogRocketService } from '@shared/services/logrocket.service';
import { ScriptProviderService } from '@shared/services/script-provider.service';
import { SmartLookService } from '@shared/services/smartlook.service';

import { environment } from '../environments/environment';
import defaultTranslations from '../locale/en-US.json';
import { HighchartsService } from './app-main/services/highcharts.service';

@Component({
    selector: 'app',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false,
})
export class AppComponent implements AfterViewInit, OnDestroy {
    private uiHelperService = inject(UiHelperService);
    private layoutConfigService = inject(LayoutConfigService);
    private router = inject(Router);
    private highchartsService = inject(HighchartsService);
    private translate = inject(TranslateService);
    private smartLookService = inject(SmartLookService);
    private logRocketService = inject(LogRocketService);
    private fingerprintService = inject(FingerprintService);
    private scriptProviderService = inject(ScriptProviderService);
    private iconService = inject(IconService);
    private integrationsService = inject(IntegrationsService);
    private toastr = inject(ToastrService);

    @ViewChild(ToastContainerDirective, { static: true })
    private toastContainer!: ToastContainerDirective;

    @ViewChild('toastLayer', { static: true })
    private toastLayer!: ElementRef<HTMLElement>;

    private toastObserver?: MutationObserver;

    constructor() {
        this.iconService.registerIcons();
        this.highchartsService.init();

        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                first(),
            )
            .subscribe(() => this.uiHelperService.hideSplashScreen());

        this.layoutConfigService.init();

        this.translate.addLangs(['en']);
        this.translate.setTranslation('en', defaultTranslations);
        this.translate.setDefaultLang('en');
        this.enableSmartLook();
        this.logRocketService.identify().subscribe();
        this.fingerprintService.load();
        this.scriptProviderService.addLinkedInScript();
        this.scriptProviderService.addBingAdsScript();
        this.scriptProviderService.addReb2bScript();
    }

    ngAfterViewInit(): void {
        this.toastr.overlayContainer = this.toastContainer;
        this.initToastPopover();
    }

    ngOnDestroy(): void {
        this.toastObserver?.disconnect();
    }

    private initToastPopover(): void {
        const element = this.toastLayer.nativeElement;

        if (!this.supportsPopover(element)) {
            return;
        }

        element.showPopover();
        this.observeToastLayer(element);
    }

    private observeToastLayer(element: HTMLElement): void {
        this.toastObserver = new MutationObserver((mutations) => {
            const toastAdded = mutations.some((mutation) => mutation.addedNodes.length > 0);

            if (toastAdded) {
                this.bringToastLayerToFront(element);
            }
        });

        this.toastObserver.observe(element, {
            childList: true,
            subtree: true,
        });
    }

    private supportsPopover(element: HTMLElement): boolean {
        return 'showPopover' in element;
    }

    private bringToastLayerToFront(element: HTMLElement): void {
        if (element.matches(':popover-open')) {
            element.hidePopover();
        }

        element.showPopover();
    }

    private enableSmartLook() {
        if (environment.production && !this.integrationsService.isIntegration(INTEGRATION.SEMRUSH)) {
            this.smartLookService.init();
            this.smartLookService.identify().subscribe();
            this.smartLookService.record();
        }
    }
}
